import type { TDocumentDefinitions, Content, TableCell, ContentTable } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import type { BscanDocumentModel } from "../../../domain/ndt/bscan-report-domain.js";
import { readImageSize, isPdfDrawableImage } from "./image-size.js";
import { HEADER_PAGE_MARGIN_TOP, INSPECTION_REPORT_HEADER_STYLES, buildInspectionReportHeader } from "./inspection-report-header.js";

/**
 * RELATÓRIO DE ENSAIO ULTRASSÔNICO B-SCAN / C-SCAN — padrão visual do RI.
 *
 * Fundo branco; número da seção em grafite escuro, título em cinza claro;
 * cabeçalho das tabelas em grafite; cabeçalho documental com QR repetido pelo
 * `header:` nativo do pdfmake. Paginação dinâmica — nenhuma quebra forçada; o
 * título de seção nunca fica sozinho no pé da página.
 *
 * Consome o BscanDocumentModel já montado pelo serviço: a Visualização e o PDF
 * leem o MESMO objeto. Nada é recalculado aqui.
 */

const W = 515;
const C = { dark: "#111827", graphite: "#374151", light: "#F3F4F6", label: "#F9FAFB", border: "#374151", text: "#111827", muted: "#4B5563" };
const LAUDO_FILL: Record<string, { fill: string; color: string }> = {
  APROVADO: { fill: "#DCFCE7", color: "#14532D" },
  REPROVADO: { fill: "#FEE2E2", color: "#7F1D1D" },
  EXAME_COMPLEMENTAR: { fill: "#FEF3C7", color: "#78350F" },
  ADPC: { fill: "#E5E7EB", color: "#111827" }
};

const GRID = {
  hLineWidth: () => 0.5, vLineWidth: () => 0.5,
  hLineColor: () => C.border, vLineColor: () => C.border,
  paddingTop: () => 2.5, paddingBottom: () => 2.5, paddingLeft: () => 3, paddingRight: () => 3
};

const v = (input: unknown): string => {
  const s = input === null || input === undefined ? "" : String(input).trim();
  return s || "-";
};

const th = (text: string): TableCell => ({ text, style: "tblHeaderCell" });

/** Mesmo desenho do banner do RI (número grafite + título cinza claro). */
function banner(number: string, title: string): Content {
  const node: ContentTable & { headlineLevel: number } = {
    headlineLevel: 1,
    table: { widths: ["5%", "95%"], body: [[{ text: number, style: "bannerNumberBox" }, { text: `  ${title}`, style: "bannerTitleBox" }]] },
    layout: { ...GRID, paddingTop: () => 3, paddingBottom: () => 3 },
    margin: [0, 8, 0, 4]
  };
  return node;
}

/** Linhas [rótulo, valor] em grade de 4 colunas (rótulo | valor | rótulo | valor). */
function pairs(rows: unknown): Content {
  if (!rows) return { text: "NÃO INFORMADO", style: "tblValueCell" };
  let entries: Array<[string, unknown]>;
  if (Array.isArray(rows)) {
    entries = rows;
  } else if (typeof rows === "object") {
    entries = Object.entries(rows);
  } else {
    return { text: "NÃO INFORMADO", style: "tblValueCell" };
  }
  if (entries.length === 0) return { text: "NÃO INFORMADO", style: "tblValueCell" };
  const body: TableCell[][] = [];
  for (let i = 0; i < entries.length; i += 2) {
    const a = entries[i]!;
    const b = entries[i + 1];
    body.push([
      { text: String(a[0] || ""), style: "tblLabelCell" }, { text: v(a[1]), style: "tblValueCell" },
      b ? { text: String(b[0] || ""), style: "tblLabelCell" } : { text: "", style: "tblLabelCell" }, b ? { text: v(b[1]), style: "tblValueCell" } : { text: "" }
    ]);
  }
  if (body.length === 0) return { text: "NÃO INFORMADO", style: "tblValueCell" };
  return { table: { widths: ["20%", "30%", "20%", "30%"], body }, layout: GRID };
}

function textBox(text: string | null | undefined): Content {
  return { table: { widths: ["*"], body: [[{ text: v(text), style: "bodyTextBlock" }]] }, layout: GRID };
}

/** Imagem dentro de `maxW × maxH`, proporção preservada. Devolve o tamanho real desenhado. */
function sized(image: string, maxW: number, maxH: number): { w: number; h: number } {
  const size = readImageSize(image);
  if (!size || size.width <= 0 || size.height <= 0) return { w: maxW, h: maxH };
  const scale = Math.min(maxW / size.width, maxH / size.height, 1);
  return { w: Math.round(size.width * scale), h: Math.round(size.height * scale) };
}

function reserved(text: string, height: number): Content {
  return { table: { widths: ["*"], heights: [height], body: [[{ text, color: C.muted, fontSize: 7.5, alignment: "center", margin: [0, height / 2 - 6, 0, 0] }]] }, layout: GRID };
}

export class BscanBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const raw = snapshot.data || {};
    // O serviço entrega o modelo puro; snapshots de revisão podem vir envelopados.
    const m = (raw.model && raw.model.results ? raw.model : raw) as BscanDocumentModel;
    // STUDIO INSPECT IA: cores do visual congelado no modelo; ausente = padrão RI de sempre.
    const vc = m.visual?.colors ?? null;
    const border = vc?.border ?? "#CBD5E1";
    const footerId = m.visual && !m.visual.footerShowReportType
      ? `INSPECT IA | ${v(m.reportNumber)} | REV.${m.revision || "00"}`
      : `INSPECT IA | Relatório B-Scan / C-Scan | ${v(m.reportNumber)} | REV.${m.revision || "00"}`;
    const institutional = m.visual?.footerInstitutionalText?.trim() || "";
    const definition: TDocumentDefinitions = {
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [40, HEADER_PAGE_MARGIN_TOP + 12, 40, 44],
      header: () => this.header(m),
      footer: (currentPage: number, pageCount: number): Content => ({
        margin: [40, 6, 40, 0],
        stack: [
          { canvas: [{ type: "line", x1: 0, y1: 0, x2: W, y2: 0, lineWidth: 0.5, lineColor: border }] },
          { margin: [0, 5, 0, 0], columns: [
            { text: footerId, style: "docFooterText" },
            { text: `Página ${currentPage} de ${pageCount}`, style: "docFooterText", alignment: "right" }
          ] },
          ...(institutional ? [{ text: institutional, style: "docFooterText", margin: [0, 2, 0, 0] } as Content] : [])
        ]
      }),
      pageBreakBefore: (currentNode: any, nodeQueries: any) =>
        currentNode.headlineLevel === 1 && nodeQueries.getFollowingNodesOnPage().length === 0,
      content: this.content(m),
      styles: {
        ...INSPECTION_REPORT_HEADER_STYLES,
        headerLogo: { fontSize: 13, bold: true, color: C.dark },
        headerDocTitle: { fontSize: 11.5, bold: true, color: C.dark, lineHeight: 1.12, margin: [0, 3, 0, 0] },
        docFooterText: { fontSize: 7.5, color: C.muted },
        bannerNumberBox: { fontSize: 8.5, bold: true, color: vc?.sectionNumberText ?? "#FFFFFF", fillColor: vc?.sectionNumberFill ?? C.dark, alignment: "center" },
        bannerTitleBox: { fontSize: 8.5, bold: true, color: vc?.sectionTitleText ?? C.dark, fillColor: vc?.sectionTitleFill ?? C.light },
        subBanner: { fontSize: 7.5, bold: true, color: C.dark, fillColor: "#E5E7EB", margin: [4, 2, 4, 2] },
        tblHeaderCell: { fontSize: 7, bold: true, color: vc?.tableHeaderText ?? "#FFFFFF", fillColor: vc?.tableHeaderFill ?? C.graphite, alignment: "center", margin: [1, 2, 1, 2] },
        tblLabelCell: { fontSize: 7.5, bold: true, color: C.text, fillColor: C.label },
        tblValueCell: { fontSize: 7.5, color: C.graphite },
        tblData: { fontSize: 7.5, color: C.text, alignment: "center" },
        tblDataLeft: { fontSize: 7.5, color: C.text },
        bodyTextBlock: { fontSize: 8, color: C.text, lineHeight: 1.25, margin: [2, 2, 2, 2] },
        caption: { fontSize: 7, color: C.graphite, alignment: "center", margin: [0, 3, 0, 0] },
        note: { fontSize: 7, color: C.muted, italics: true, margin: [0, 3, 0, 0] }
      },
      defaultStyle: { fontSize: 8, font: "Roboto" }
    };
    return definition;
  }

  private header(m: BscanDocumentModel): Content {
    return buildInspectionReportHeader({
      title: m.title || "RELATÓRIO DE ENSAIO ULTRASSÔNICO\nB-SCAN / C-SCAN",
      subtitle: "Sistema de Inspeção e Integridade Mecânica",
      qrReference: m.qrPayload || m.reportNumber || "INSPECT-IA",
      fields: [
        { label: "Relatório nº", value: m.reportNumber },
        { label: "Rel. cliente nº", value: m.clientReportNumber },
        { label: "Revisão", value: m.revision || "00" },
        { label: "Data da emissão", value: m.issueDate },
        { label: "OS / Sub-OS", value: m.identification?.osSubOs }
      ],
      unitArea: m.identification?.unitArea || "",
      tag: m.identification?.equipmentTag || "",
      visual: m.visual ?? null
    });
  }

  private content(m: BscanDocumentModel): Content[] {
    const t = m.transducer;
    const out: Content[] = [];
    if (m.subtitle) out.push({ text: m.subtitle, fontSize: 8, bold: true, color: C.graphite, margin: [0, 0, 0, 4] });
    out.push(banner("01", "IDENTIFICAÇÃO"), pairs(m.identificationRows || []));
    out.push(banner("02", "OBJETIVO"), textBox(m.objective));
    out.push(banner("03", "DADOS TÉCNICOS DO ENSAIO"), pairs(m.technicalData || []));
    out.push(banner("04", "EQUIPAMENTO, TRANSDUTOR E CALIBRAÇÃO"), pairs(m.equipment || []));
    if (t) {
      out.push({
        margin: [0, 4, 0, 0],
        table: { headerRows: 1, widths: ["*", "*", "*", "*", "*", "*"], body: [
          [th("TRANSDUTOR — FABRICANTE"), th("MODELO"), th("FREQUÊNCIA"), th("ÂNGULO"), th("DIMENSÃO"), th("Nº DE SÉRIE")],
          [t.manufacturer, t.model, t.frequency, t.angle, t.dimension, t.serialNumber].map(x => ({ text: v(x), style: "tblData" }))
        ] },
        layout: GRID
      });
    }
    out.push(banner("05", "CRITÉRIO DE ACEITAÇÃO"), pairs(m.acceptance || []));
    out.push(banner("06", "ÁREAS DE VARREDURA E RESULTADOS"), this.results(m));
    out.push(banner("07", "ISOMÉTRICO / CROQUI"), this.drawing(m));
    out.push(banner("08", "REGISTROS FOTOGRÁFICOS"), ...this.photos(m));
    out.push(banner("09", "IMAGENS B-SCAN / C-SCAN"), ...this.scanImages(m));
    out.push(banner("10", "OBSERVAÇÕES"));
    out.push(this.observations(m));
    out.push(banner("11", "RESPONSÁVEIS"), this.signatures(m));
    return out;
  }

  private results(m: BscanDocumentModel): Content {
    const rows = m.results || [];
    const body: TableCell[][] = [[th("ITEM"), th("ÁREA"), th("IDENTIFICAÇÃO"), th("EXTENSÃO"), th("ESP. NOMINAL"), th("ESP. MÍN. PROJETO"), th("ESP. MÍNIMA MEDIDA"), th("LAUDO"), th("OBSERVAÇÃO")]];
    if (rows.length === 0) body.push([{ text: "Nenhum resultado registrado.", colSpan: 9, style: "tblData" }, {}, {}, {}, {}, {}, {}, {}, {}]);
    for (const r of rows) {
      const fill = LAUDO_FILL[r.laudo];
      body.push([
        { text: v(r.item), style: "tblData" }, { text: v(r.areaCode), style: "tblData", bold: true }, { text: v(r.identification), style: "tblDataLeft" },
        { text: v(r.extent), style: "tblData" }, { text: v(r.nominalThickness), style: "tblData" }, { text: v(r.minDesignThickness), style: "tblData" },
        { text: v(r.minimumThickness), style: "tblData", bold: true },
        { text: v(r.laudoAbbreviation), style: "tblData", bold: true, ...(fill ? { fillColor: fill.fill, color: fill.color } : {}) },
        { text: v(r.observation), style: "tblDataLeft" }
      ]);
    }
    return {
      stack: [
        { table: { headerRows: 1, dontBreakRows: true, widths: [24, 50, "*", 50, 44, 48, 48, 34, "*"], body }, layout: GRID },
        { text: m.bscanLegend || "", style: "note" },
        ...(m.temperatureNote ? [{ text: m.temperatureNote, style: "note" } as Content] : [])
      ]
    };
  }

  /** Marcadores posicionados sobre a imagem em fração (0..1) — fora do fluxo, sem empurrar o layout. */
  private drawing(m: BscanDocumentModel): Content {
    const d = m.drawing;
    if (!d || !isPdfDrawableImage(d.image)) return reserved("Nenhum isométrico anexado.", 60);
    const { w, h } = sized(d.image, W - 10, 420);
    const markers: Content[] = (d.markers || []).map(mk => ({
      relativePosition: { x: Math.max(0, mk.x * w - 14), y: -h + Math.max(0, mk.y * h - 6) },
      table: { body: [[{ text: mk.label, fontSize: 6.5, bold: true, color: "#FFFFFF", fillColor: "#8A5A1F", margin: [2, 0, 2, 0] }]] },
      layout: "noBorders"
    } as unknown as Content));
    return {
      unbreakable: true,
      stack: [
        { columns: [{ width: "*", text: "" }, { width: w, stack: [{ image: d.image, width: w, height: h }, ...markers] }, { width: "*", text: "" }] },
        { text: [d.reference ? `Desenho: ${d.reference}` : "", d.caption].filter(Boolean).join(" — ") || d.fileName || "", style: "caption" }
      ]
    };
  }

  /** Duas fotos por linha; a linha nunca se parte entre páginas. */
  private photos(m: BscanDocumentModel): Content[] {
    const groups = (m.photoGroups || []).filter(g => g.photos.length > 0);
    if (groups.length === 0) return [reserved("Nenhum registro fotográfico.", 40)];
    const cellW = (W - 20) / 2;
    const cell = (p: { image: string; caption: string } | undefined): TableCell => {
      if (!p) return { text: "" };
      const img: Content = isPdfDrawableImage(p.image) ? (() => { const s = sized(p.image, cellW - 8, 170); return { image: p.image, width: s.w, height: s.h, alignment: "center" } as Content; })() : { text: "Imagem em formato não suportado", style: "caption" };
      return { stack: [img, { text: p.caption || "", style: "caption" }] };
    };
    const out: Content[] = [];
    for (const g of groups) {
      const body: TableCell[][] = [[{ text: g.title, style: "subBanner", colSpan: 2 }, {}]];
      for (let i = 0; i < g.photos.length; i += 2) body.push([cell(g.photos[i]), cell(g.photos[i + 1])]);
      out.push({ table: { headerRows: 1, dontBreakRows: true, widths: ["*", "*"], body }, layout: GRID, margin: [0, 0, 0, 6] });
    }
    return out;
  }

  /** Uma imagem por bloco, na largura útil. */
  private scanImages(m: BscanDocumentModel): Content[] {
    const list = m.scanImages || [];
    if (list.length === 0) return [reserved("Nenhuma imagem B-Scan / C-Scan anexada.", 40)];
    return list.map(img => {
      if (!isPdfDrawableImage(img.image)) return { stack: [reserved("Imagem em formato não suportado pelo documento.", 40), { text: img.caption, style: "caption" }], margin: [0, 0, 0, 8] } as Content;
      const s = sized(img.image, W - 10, 360);
      return { unbreakable: true, margin: [0, 0, 0, 8], table: { widths: ["*"], body: [[{ stack: [{ image: img.image, width: s.w, height: s.h, alignment: "center" }, { text: img.caption || "", style: "caption" }] }]] }, layout: GRID } as Content;
    });
  }

  private observations(m: BscanDocumentModel): Content {
    return {
      table: { widths: ["20%", "80%"], body: [
        [{ text: "GERAIS", style: "tblLabelCell" }, { text: v(m.generalObservations), style: "bodyTextBlock" }],
        [{ text: "TÉCNICAS", style: "tblLabelCell" }, { text: v(m.technicalObservations), style: "bodyTextBlock" }]
      ] },
      layout: GRID
    };
  }

  private signatures(m: BscanDocumentModel): Content {
    const cols = m.signatureColumns || [];
    if (cols.length === 0) return reserved("Signatários não definidos.", 40);
    const sigW = (W - cols.length * 6) / cols.length - 6;
    // Bloco visual de assinatura (Studio): o nome nunca é ocultado.
    const show = m.visual?.signature ?? { showImage: true, showName: true, showRegistration: true, showFunction: true, showDate: true };
    return {
      unbreakable: true,
      stack: [
        { text: m.responsibilityTerm || "", style: "note", margin: [0, 0, 0, 4] },
        { table: { widths: cols.map(() => "*"), body: [
          cols.map(c => th(c.roleLabel)),
          cols.map(c => ({ stack: [
            ...(show.showImage ? [c.signatureImage && isPdfDrawableImage(c.signatureImage)
              ? (() => { const s = sized(c.signatureImage!, sigW, 40); return { image: c.signatureImage!, width: s.w, height: s.h, alignment: "center" } as Content; })()
              : { text: " ", margin: [0, 18, 0, 18] } as Content] : [{ text: " ", margin: [0, 18, 0, 18] } as Content]),
            { canvas: [{ type: "line", x1: 0, y1: 0, x2: sigW, y2: 0, lineWidth: 0.5, lineColor: C.graphite }], margin: [0, 2, 0, 2] },
            { text: v(c.name), fontSize: 7.5, bold: true, alignment: "center" },
            ...(show.showFunction ? [{ text: c.functionTitle || "", fontSize: 7, alignment: "center", color: C.graphite } as Content] : []),
            ...(show.showRegistration ? [{ text: c.credential || "", fontSize: 7, alignment: "center", color: C.graphite } as Content] : []),
            ...(show.showDate ? [{ text: c.date ? `Data: ${c.date}` : "Data: ____/____/______", fontSize: 7, alignment: "center", color: C.muted } as Content] : [])
          ] } as TableCell))
        ] }, layout: GRID }
      ]
    };
  }
}

documentEngine.registerBuilder("BSCAN", new BscanBuilder());
